import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  LlmModel,
  LlmModelCreate,
  LlmModelUpdate,
} from "@/api/types";

const KEY = ["llm-models"] as const;

export function useLlmModels(filters: { include_inactive?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () =>
      api.GET<LlmModel[]>("/llm-models", {
        params: { include_inactive: Boolean(filters.include_inactive) },
      }),
  });
}

export function useLlmModel(modelId: string) {
  return useQuery({
    queryKey: [...KEY, modelId],
    queryFn: () => api.GET<LlmModel>(`/llm-models/${modelId}`),
    enabled: Boolean(modelId),
  });
}

export function useCreateLlmModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LlmModelCreate) =>
      api.POST<LlmModel>("/llm-models", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateLlmModel(modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: LlmModelUpdate) =>
      api.PATCH<LlmModel>(`/llm-models/${modelId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeactivateLlmModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modelId: string) =>
      api.DELETE<void>(`/llm-models/${modelId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
